import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Droplets, Leaf, Loader2, MessageCircle } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";

export default function ProductDetails() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <section className="section-pad">
        <div className="container-xl text-center" data-testid="product-not-found">
          <Leaf className="mx-auto h-12 w-12 text-primary/30" />
          <h1 className="mt-4 font-serif text-3xl">Product not found</h1>
          <p className="mt-2 text-muted-foreground">This remedy may have been removed or the link is incorrect.</p>
          <Link to="/products" className="btn-primary mt-6 inline-flex" data-testid="back-to-products-button">
            <ArrowLeft className="h-4 w-4" /> Browse all products
          </Link>
        </div>
      </section>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="product-details-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const benefits = (product.benefits || "").split("\n").map((b) => b.trim()).filter(Boolean);

  return (
    <section className="section-pad" data-testid="product-details-page">
      <div className="container-xl">
        <Link to="/products" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline" data-testid="product-details-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>

        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl border border-border bg-secondary shadow-sm">
            {product.image_url ? (
              <img
                src={assetUrl(product.image_url)}
                alt={product.name}
                className="aspect-[4/3] h-full w-full object-cover"
                data-testid="product-details-image"
              />
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center text-primary/30">
                <Leaf className="h-16 w-16" />
              </div>
            )}
          </div>

          <div>
            {product.category && (
              <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-primary" data-testid="product-details-category">
                {product.category}
              </span>
            )}
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl" data-testid="product-details-name">{product.name}</h1>
            {product.price && (
              <div className="mt-3 text-2xl font-700 text-[var(--brand-navy)]" data-testid="product-details-price">{product.price}</div>
            )}
            {product.description && (
              <p className="mt-5 leading-relaxed text-muted-foreground" data-testid="product-details-description">{product.description}</p>
            )}

            {benefits.length > 0 && (
              <div className="mt-8" data-testid="product-details-benefits">
                <h2 className="font-serif text-lg font-600">Key Benefits</h2>
                <ul className="mt-3 space-y-2.5">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.dosage && (
              <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="product-details-dosage">
                <div className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-accent" />
                  <h2 className="font-serif text-lg font-600">Dosage & Directions</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{product.dosage}</p>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="product-details-enquire-button">
                <MessageCircle className="h-4 w-4" /> Enquire about this remedy
              </Link>
            </div>
            <ShareButtons productName={product.name} />
            <p className="mt-4 text-xs text-muted-foreground">
              Always consult a qualified homeopathic practitioner before starting any new remedy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
